import { Table } from '@chakra-ui/react';
import { AppLink } from '@cyberkongz/ui';

type ProviderType = 'Cloudflare' | 'Google' | 'ClickDimensions' | 'Amplitude, Inc.';

type CookieType = {
  cookie: string;
  provider: ProviderType;
  description: string;
  type: string;
  lifespan: string;
};

const PROVIDER_LINKS: { [key in ProviderType]: string } = {
  Cloudflare:
    'https://developers.cloudflare.com/fundamentals/get-started/reference/cloudflare-cookies/',
  Google: 'https://developers.google.com/analytics/devguides/collection/gtagjs/cookie-usage',
  ClickDimensions:
    'https://support.clickdimensions.com/hc/en-us/articles/115001156993-ClickDimensions-Cookies-for-Web-Analytics',
  [`Amplitude, Inc.`]: 'https://amplitude.com/privacy',
};

const NECESSARY_COOKIES: CookieType[] = [
  {
    cookie: '__cfruid',
    provider: 'Cloudflare',
    description:
      'Cloudflare cookie used in cases where multiple users are hidden behind the same client IP address. Each user will have a unique cookie to help prevent a case of rate limiting all users if one particular user hits the rate limit threshold.',
    type: 'First Party',
    lifespan: 'Session',
  },
  {
    cookie: '__cfduid',
    provider: 'Cloudflare',
    description:
      'This cookie collects and anonymizes End User IP addresses using a one-way hash of certain values so they cannot be personally identified. The cookie is a session cookie that expires after 30 days. The _cfduid cookie does not allow for cross-site tracking, following users from site to site by merging various \\cfduid identifiers into a profile or corresponding to any user ID in a Customer’s web application.',
    type: 'First Party',
    lifespan: '(0 days) only during session',
  },
  {
    cookie: 'cf_chl_XXXX',
    provider: 'Cloudflare',
    description:
      'Used by Cloudflare for the execution of Javascript or Captcha challenges. These cookies are not used for tracking or beyond the scope of the challenge',
    type: 'First Party',
    lifespan: "The storage duration dependents on the End User's Browser Policy",
  },
  {
    cookie: '_cf_bm',
    provider: 'Cloudflare',
    description:
      'This cookie is used to distinguish between humans and bots. This is beneficial for the website, in order to make valid reports on the use of their website.',
    type: 'First Party',
    lifespan: '30 minutes',
  },
  {
    cookie: 'cf_clearance',
    provider: 'Cloudflare',
    description:
      'Clearance Cookie stores the proof of challenge passed. It is used to no longer issue a captcha or jschallenge challenge if present. It is required to reach origin server.',
    type: 'First Party',
    lifespan: '30 seconds',
  },
  {
    cookie: '__cflb',
    provider: 'Cloudflare',
    description:
      'The __cflb cookie allows Cloudflare to return an end user to the same customer origin for a specific period of time configured by the customer. This allows the end user to have a seamless experience (SmartSurvey)',
    type: 'FirstParty',
    lifespan: 'Session',
  },
  {
    cookie: 'cf_ob_info',
    provider: 'Cloudflare',
    description:
      'The cf_ob_info cookie is set by Cloudflare to provide information on HTTP Status Code returned by the origin web server, the Ray ID of the original failed request and the data center serving the traffic.',
    type: 'First Party',
    lifespan: '30 seconds',
  },
  {
    cookie: 'cf_use_ob',
    provider: 'Cloudflare',
    description:
      "Cloudflare sets this cookie to improve page load times and to disallow any security restrictions based on the visitor's IP address.",
    type: 'First Party',
    lifespan: '30 seconds',
  },
  {
    cookie: '__cfwaitingroom',
    provider: 'Cloudflare',
    description:
      'Cookie is only used to track visitors that access a waiting room-enabled host and path combination for a zone. Visitors using a browser that does not accept cookies cannot visit the host and path combination while the waiting room is active.',
    type: 'First Party',
    lifespan: '20 seconds',
  },
  {
    cookie: '_cfuvid',
    provider: 'Cloudflare',
    description:
      'This cookie is a part of the services provided by Cloudflare - Including load-balancing, deliverance of website content and serving DNS connection for website operators.',
    type: 'First Party',
    lifespan: 'Session',
  },
];

const ANALYTICS_COOKIES: CookieType[] = [
  {
    cookie: '_gid*',
    provider: 'Google',
    description:
      'This cookie is set by Google Analytics. It stores and updates a unique value for each page visited and is used to count and track pageviews.',
    type: 'First & Third party',
    lifespan: '1 Day',
  },
  {
    cookie: '_gat',
    provider: 'Google',
    description:
      'This cookie name is associated with Google Universal Analytics, according to Google documentation it is used to throttle the request rate - limiting the collection of data on high traffic sites.',
    type: 'First Party',
    lifespan: '1 Minute',
  },
  {
    cookie: '_ga*',
    provider: 'Google',
    description:
      'This cookie name is associated with Google Universal Analytics - which is a significant update to Google’s more commonly used analytics service. This cookie is used to distinguish unique users by assigning a randomly generated number as a client identifier. It is included in each page request in a site and used to calculate visitor, session and campaign data for the sites analytics reports.',
    type: 'First & Third party',
    lifespan: '2 years',
  },
  {
    cookie: 'cuvid',
    provider: 'ClickDimensions',
    description:
      'This cookie is typically written to the browser upon the first visit to the site from that web browser. If the cookie has been deleted by the browser operator, and the browser subsequently visits the site, a new __cuid cookie is written with a different visitor unique ID. This cookie is used to determine unique visitors to the site and it is updated with each page view. Additionally, this cookie is provided with a unique ID that the application uses to ensure both the validity and accessibility of the cookie as an extra security measure.',
    type: 'Third party',
    lifespan: '2 years',
  },
  {
    cookie: 'amp_token',
    provider: 'Google',
    description:
      'It contains a token that can be used to retrieve a Client ID from AMP Client ID service. Other possible values indicate opt-out, inflight request or an error retrieving a Client ID from AMP Client ID service.',
    type: 'Third party',
    lifespan: '30 seconds to 1 year',
  },
  {
    cookie: 'amp_cookie_test',
    provider: 'Amplitude, Inc.',
    description: 'These cookies are used by Amplitude for session tracking for analytics purposes.',
    type: 'Third party',
    lifespan: 'Session',
  },
];

function CookiesTable({ cookies }: { cookies: CookieType[] }) {
  return (
    <Table.Root
      mb={5}
      size="sm"
      css={{
        '& td, & th': {
          p: [1, 2],
          borderRight: '1px solid',
          '&:first-of-type': {
            borderLeft: '1px solid',
          },
        },
        '& th': { borderTop: '1px solid' },
      }}
    >
      <Table.Header>
        <Table.Row
          css={{
            '& th': {
              color: 'text',
              fontSize: ['xs', 'sm'],
              fontFamily: 'heading',
              fontWeight: 'bold',
            },
          }}
        >
          <Table.ColumnHeader>Cookie</Table.ColumnHeader>
          <Table.ColumnHeader>Provider</Table.ColumnHeader>
          <Table.ColumnHeader>Description</Table.ColumnHeader>
          <Table.ColumnHeader>Type</Table.ColumnHeader>
          <Table.ColumnHeader>Lifespan</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body verticalAlign="baseline" whiteSpace="initial">
        {cookies.map(({ cookie, description, type, provider, lifespan }) => (
          <Table.Row key={cookie}>
            <Table.Cell width="8.75em">{cookie}</Table.Cell>
            <Table.Cell width="5em">
              <AppLink href={PROVIDER_LINKS[provider]} css={{ textDecoration: 'underline' }}>
                {provider}
              </AppLink>
            </Table.Cell>
            <Table.Cell minWidth={['20em', '0']}>{description}</Table.Cell>
            <Table.Cell width="6.25em">{type}</Table.Cell>
            <Table.Cell width="5em">{lifespan}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

export function NecessaryCookiesTable() {
  return <CookiesTable cookies={NECESSARY_COOKIES} />;
}

export function AnalyticsCookiesTable() {
  return <CookiesTable cookies={ANALYTICS_COOKIES} />;
}
